import { Controller } from "@hotwired/stimulus"

// Compact time picker: 1-12 hour select, :00/:15/:30/:45 minute select,
// and an AM/PM segmented toggle. Writes back to a hidden HH:MM (24-hour)
// input so server-side parsing matches the existing styled_select contract.
//
// Targets:
//   hidden  — hidden input that carries the submitted "HH:MM" value
//   hour    — <select> with values 1..12
//   minute  — <select> with values "00", "15", "30", "45"
//   amBtn   — AM toggle button (data-active="true|false")
//   pmBtn   — PM toggle button (data-active="true|false")
export default class extends Controller {
  static targets = ["hidden", "hour", "minute", "amBtn", "pmBtn"]

  connect() {
    // Initial paint reflects whatever the server rendered (prefilled or empty).
    // No need to rewrite the hidden — the server already set it correctly.
  }

  setAm() {
    this._setMeridiem("am")
    this._sync()
  }

  setPm() {
    this._setMeridiem("pm")
    this._sync()
  }

  hourChanged() {
    this._maybeAutoMeridiem()
    this._sync()
  }

  minuteChanged() {
    this._maybeAutoMeridiem()
    this._sync()
  }

  // If the user picks an hour/minute before touching AM/PM, default to AM
  // so a single-tap AM workflow still produces a complete value.
  _maybeAutoMeridiem() {
    if (this._currentMeridiem()) return
    if (!this.hourTarget.value || !this.minuteTarget.value) return
    this._setMeridiem("am")
  }

  _sync() {
    const h12 = parseInt(this.hourTarget.value, 10)
    const minute = parseInt(this.minuteTarget.value, 10)
    const meridiem = this._currentMeridiem()
    const previous = this.hiddenTarget.value

    let next = ""
    if (!Number.isNaN(h12) && !Number.isNaN(minute) && meridiem) {
      let h24 = h12 % 12
      if (meridiem === "pm") h24 += 12
      next = `${String(h24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`
    }

    if (next === previous) return
    this.hiddenTarget.value = next
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _setMeridiem(value) {
    const isAm = value === "am"
    this.amBtnTarget.dataset.active = isAm ? "true" : "false"
    this.pmBtnTarget.dataset.active = isAm ? "false" : "true"
    this.amBtnTarget.setAttribute("aria-pressed", isAm ? "true" : "false")
    this.pmBtnTarget.setAttribute("aria-pressed", isAm ? "false" : "true")
  }

  _currentMeridiem() {
    if (this.amBtnTarget.dataset.active === "true") return "am"
    if (this.pmBtnTarget.dataset.active === "true") return "pm"
    return null
  }
}
